import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CreditCard, ArrowLeft, Search, CheckCircle,
  XCircle, Clock, Filter,
} from 'lucide-react';
import { callAdminGetPaymentHistory } from '../../services/admin';

type Payment = {
  id: string;
  uid: string;
  pfPaymentId: string;
  paymentStatus: string;
  productId: string;
  planId: string;
  amountGross: string;
  amountFee: string;
  amountNet: string;
  itemName: string;
  emailAddress: string;
  createdAt: any;
  adminGenerated: boolean;
};

const statusIcon = (status: string) => {
  const s = (status || '').toUpperCase();
  if (s === 'COMPLETE' || s === 'ACTIVE') return <CheckCircle className="w-4 h-4 text-green-500" />;
  if (s === 'CANCELLED' || s === 'FAILED') return <XCircle className="w-4 h-4 text-red-500" />;
  return <Clock className="w-4 h-4 text-slate-400" />;
};

const AdminPayments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [productFilter, setProductFilter] = useState<string>('all');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { payments: list } = await callAdminGetPaymentHistory({ limit: 100 });
        if (mounted) setPayments(list);
      } catch {
        // ignore
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const formatDate = (ts: any) => {
    if (!ts) return '—';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filtered = payments.filter(p => {
    if (productFilter !== 'all' && p.productId !== productFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.emailAddress || '').toLowerCase().includes(q) ||
      (p.uid || '').toLowerCase().includes(q) ||
      (p.pfPaymentId || '').toLowerCase().includes(q) ||
      (p.itemName || '').toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <motion.div className="glass-effect rounded-2xl p-8 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-600 dark:text-gray-300">Loading payments...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <CreditCard className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">All Payments</h1>
              <p className="text-slate-600 dark:text-gray-400">{payments.length} transactions</p>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          className="flex flex-col md:flex-row gap-4 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by email, UID, or payment ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            {['all', 'vetaxis', 'staffaxis', 'noteaxis', 'suite', 'custom'].map(f => (
              <button
                key={f}
                onClick={() => setProductFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize ${
                  productFilter === f
                    ? 'bg-primary-500 text-white'
                    : 'glass-effect text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Payment Table */}
        <motion.div
          className="glass-effect rounded-2xl shadow-glass overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <CreditCard className="w-12 h-12 text-slate-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-gray-400">
                {search || productFilter !== 'all' ? 'No payments match your filters.' : 'No payments recorded yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-gray-700">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Fee</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                  {filtered.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50/50 dark:hover:bg-gray-800/30 transition-colors">
                      <td className="px-6 py-4 text-sm text-slate-700 dark:text-gray-300 whitespace-nowrap">
                        {formatDate(p.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 dark:text-gray-300 whitespace-nowrap">
                        <span className="truncate max-w-[180px] inline-block" title={p.emailAddress || p.uid}>
                          {p.emailAddress || p.uid.substring(0, 12) + '...'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 dark:text-gray-300 capitalize whitespace-nowrap">
                        {p.productId}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 dark:text-gray-300 capitalize whitespace-nowrap">
                        {p.planId}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-white whitespace-nowrap">
                        {p.amountGross ? `R${p.amountGross}` : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-gray-400 whitespace-nowrap">
                        {p.amountFee ? `R${p.amountFee}` : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                          {statusIcon(p.paymentStatus)}
                          <span className="capitalize">{(p.paymentStatus || 'pending').toLowerCase()}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {p.adminGenerated ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                            Admin Link
                          </span>
                        ) : (
                          <span className="text-xs text-slate-500 dark:text-gray-400">Direct</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPayments;
